import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Search } from 'lucide-react';
import { useRecipes } from '../hooks/useRecipes';
import { RecipeCard } from '../components/RecipeCard';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { RECIPE_TYPE_OPTIONS } from '../constants';

const RECIPE_TYPES = [{ value: '', label: 'All' }, ...RECIPE_TYPE_OPTIONS] as const;

export function RecipeListPage() {
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { recipes, loading, error, hasMore, loadMore } = useRecipes({
    type: typeFilter || undefined,
    search: search || undefined,
    admin: true,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">Crafting Recipes</h1>
          <FavoriteButton archeId="arche_crafting" />
        </div>
        <Link to="/a/arche_crafting/recipes/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Recipe
          </Button>
        </Link>
      </div>

      <Tabs value={typeFilter} onValueChange={setTypeFilter}>
        <TabsList className="flex-wrap h-auto gap-1">
          {RECIPE_TYPES.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search recipes..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
                aria-label="Search recipes"
              />
            </div>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </div>

        {/* Single content area that responds to type filter */}
        <TabsContent value={typeFilter} forceMount className="mt-4">
          {error && <p className="text-sm text-destructive">{error}</p>}

          {loading && recipes.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-36 rounded-lg" />
              ))}
            </div>
          ) : recipes.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No recipes found.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recipes.map((recipe) => (
                  <RecipeCard key={recipe.recipeId} recipe={recipe} />
                ))}
              </div>
              {hasMore && (
                <div className="text-center mt-4">
                  <Button variant="ghost" onClick={loadMore}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
